import React, { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { DirectBillingPayerGroupDto } from "../../../../../../api-client";
import { ValidationMap } from "../types/interface";
import { colors, fontSize, spacing } from "../styles/tokens";

/** A single stat: number on top, label below. Optional highlight color on the number */
const Stat: FC<{ label: string; value: number; highlight?: string }> = ({
  label,
  value,
  highlight,
}) => (
  <Box sx={{ textAlign: "center", px: 1 }}>
    <Typography
      sx={{
        fontSize: "1.2rem",
        fontWeight: 700,
        lineHeight: 1,
        color: highlight || colors.textOnDark,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.7rem",
        color: colors.textOnDark,
        lineHeight: 1,
        mt: 0.2,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Subtle vertical separator between stats */
const Divider = () => (
  <Box sx={{ width: "1px", height: 20, backgroundColor: "rgba(255,255,255,0.2)" }} />
);

export interface GkvBillSummaryProps {
  bills: DirectBillingPayerGroupDto[];
  validationMap: ValidationMap;
}

/**
 * Summary bar showing key metrics for the current bill set.
 *
 * Computes totals from lightweight DTOs + external validationMap
 * (unlike the old hook which read `rx.formvalidation` inline).
 */
const GkvBillSummary: FC<GkvBillSummaryProps> = ({ bills, validationMap }) => {
  const { totalRxs, passedValidationRxs, problematicRxs, taxiertRxs, numberOfPayers } =
    useMemo(() => {
      let totalRxs = 0;
      let passedValidationRxs = 0;
      let problematicRxs = 0;
      let taxiertRxs = 0;
      const payers = new Set<string>();

      bills.forEach((group) => {
        payers.add(group.payerIK);
        group.rxs.forEach((rx) => {
          totalRxs++;

          const rxValidation = validationMap[rx.id];
          if (rxValidation) {
            const allValid = Object.values(rxValidation).every((v) => v.validity === "Valid");
            if (allValid) {
              passedValidationRxs++;
            } else {
              problematicRxs++;
            }
          }

          if (rx.isTaxieren) {
            taxiertRxs++;
          }
        });
      });

      return {
        totalRxs,
        passedValidationRxs,
        problematicRxs,
        taxiertRxs,
        numberOfPayers: payers.size,
      };
    }, [bills, validationMap]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: colors.textOnDark,
          letterSpacing: "0.3px",
          whiteSpace: "nowrap",
        }}
      >
        Zusammenfassung
      </Typography>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          ml: 2,
        }}
      >
        <Stat label="Rezepte" value={totalRxs} />
        <Divider />
        <Stat label="Valide" value={passedValidationRxs} />
        <Divider />
        <Stat label="Ungültig" value={problematicRxs} />
        <Divider />
        <Stat label="Taxiert" value={taxiertRxs} />
        <Divider />
        <Stat label="Kostenträger" value={numberOfPayers} />
      </Box>
    </Box>
  );
};

export default GkvBillSummary;
